import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RefreshCw, CheckCircle } from "lucide-react";
import { useAccount, useReadContract } from "wagmi";
import { erc20Abi, parseEther } from "viem";
import {
  writeContract,
  readContract,
  simulateContract,
  waitForTransactionReceipt,
} from "@wagmi/core";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatEtherWithDecimals, formatNumberInput } from "@/lib/utils";
import { showError, showSuccess, showLoading, dismissToast } from "@/lib/toast";
import {
  USDT_ADDRESS,
  BUY_FUSD_BRI_CONTRACT_ADDRESS,
  CCC_FUSD_ADDRESS,
} from "@/config/env";
import { wagmiConfig } from "@/config";
import { BscBuyFUSDBriAbi } from "@/abi/BscBuyFUSDBri.abi";
import { parseChainError } from "@/lib/chainError";
import { useSubmitWithdrawOrDepositTx } from "@/hooks/api/useSubmitWithdrawOrDepositTx";
import { useQueryClient } from "@tanstack/react-query";
import { C } from "./profileTheme";
import { useTranslation } from "react-i18next";
import useFusdtPrice from "@/hooks/api/useFusdPrice";
import { apiRequest } from "@/server/request";
import { getTokenName } from "@/config/common";

export function BuyFUSDDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { address } = useAccount();
  const queryClient = useQueryClient();
  const { mutateAsync: submitTx } = useSubmitWithdrawOrDepositTx();
  const [amount, setAmount] = useState("");
  const [buyFusdLoading, setBuyFusdLoading] = useState(false);
  const numAmount = parseFloat(amount) || 0;
  const { data: fusdtPrice } = useFusdtPrice();
  const { data: walletUsdtBalance, refetch: refetchBalance } = useReadContract({
    address: USDT_ADDRESS as `0x${string}`,
    abi: erc20Abi,
    functionName: "balanceOf",
    args: address ? [address] : undefined,
  });

  const balanceWei = (walletUsdtBalance as bigint) ?? BigInt(0);
  const balanceFormatted = formatEtherWithDecimals(balanceWei);

  useEffect(() => {
    if (open) {
      refetchBalance();
    } else {
      setAmount("");
    }
  }, [open, refetchBalance]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value && !/^\d*\.?\d*$/.test(value)) return;
    const max = Number(balanceFormatted);
    const [formatted, isGtMax] = formatNumberInput(value, max);
    setAmount(formatted);
    if (isGtMax) showError(t("profilePage.depositDialog.insufficientBalance"));
  };

  const handleConfirm = async () => {
    if (!address) {
      showError(t("profilePage.depositDialog.connectWalletFirst"));
      return;
    }
    if (!amount || numAmount <= 0) return;
    if (numAmount > Number(balanceFormatted)) {
      showError(t("profilePage.depositDialog.insufficientBalance"));
      return;
    }

    const bridgeAddress = BUY_FUSD_BRI_CONTRACT_ADDRESS as `0x${string}`;
    const tokenAddress = USDT_ADDRESS as `0x${string}`;
    const amountWei = parseEther(amount).toString();

    try {
      setBuyFusdLoading(true);
      showLoading(t("profilePage.buyFusdDialog.processing"), "buy-fusd");

      const currentAllowance = await readContract(wagmiConfig, {
        address: tokenAddress,
        abi: erc20Abi,
        functionName: "allowance",
        args: [address, bridgeAddress],
      });

      if ((currentAllowance as bigint) < BigInt(amountWei)) {
        const approveTx = await writeContract(wagmiConfig, {
          address: tokenAddress,
          abi: erc20Abi,
          functionName: "approve",
          args: [bridgeAddress, BigInt(amountWei)],
        });
        await waitForTransactionReceipt(wagmiConfig, { hash: approveTx });
      }

      const req = await simulateContract(wagmiConfig, {
        address: bridgeAddress,
        abi: BscBuyFUSDBriAbi,
        functionName: "depositForBuyToken",
        args: [BigInt(amountWei)],
      });
      const hash = await writeContract(wagmiConfig, req.request);
      await waitForTransactionReceipt(wagmiConfig, { hash });
      dismissToast("buy-fusd");

      await submitTx({
        txId: hash,
        amount: amountWei,
        opCode: 3,
        coinContract: USDT_ADDRESS,
      });
      showSuccess(t("profilePage.buyFusdDialog.success"));
      queryClient.invalidateQueries({ queryKey: ["balanceV2"] });
      refetchBalance();
      onClose();
    } catch (err) {
      const errorKey = parseChainError(err, "profilePage.buyFusdDialog.failed");
      showError(t(errorKey));
    } finally {
      dismissToast("buy-fusd");
      setBuyFusdLoading(false);
    }
  };

  const handleClose = () => {
    if (buyFusdLoading) return;
    setAmount("");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-sm mx-auto"
        style={{
          background: "#0d0f14",
          border: "1px solid rgba(34,197,94,0.20)",
          color: C.text,
        }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: C.primary }}>
            {t("profilePage.buyFusdDialog.title", {
              tokenName: getTokenName(CCC_FUSD_ADDRESS),
            })}
          </DialogTitle>
        </DialogHeader>
        <AnimatePresence mode="wait">
          <motion.div
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            <div
              className="rounded-lg p-3 text-xs space-y-2"
              style={{ background: C.card, border: `1px solid ${C.border}` }}
            >
              <div className="flex justify-between">
                <span style={{ color: C.dim }}>
                  {t("profilePage.buyFusdDialog.availableUsdt", {
                    tokenNameUsdt: getTokenName(USDT_ADDRESS),
                  })}
                </span>
                <span className="mono-num">
                  {balanceFormatted} {getTokenName(USDT_ADDRESS)}
                </span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: C.dim }}>
                  {t("profilePage.buyFusdDialog.exchangeRate")}
                </span>
                <span>
                  1 {getTokenName(USDT_ADDRESS)} ≈{" "}
                  {(1 / fusdtPrice!).toFixed(2)}{" "}
                  {getTokenName(CCC_FUSD_ADDRESS)}
                </span>
              </div>
              {/* <div className="flex justify-between">
                  <span style={{ color: C.dim }}>{t("profilePage.buyFusdDialog.deductionSource")}</span>
                  <span>{t("profilePage.buyFusdDialog.deductionSourceValue")}</span>
                </div> */}
            </div>
            <div>
              <p className="text-xs mb-2" style={{ color: C.dim }}>
                {t("profilePage.buyFusdDialog.purchaseAmount")}
              </p>
              <div className="relative">
                <input
                  type="text"
                  inputMode="decimal"
                  value={amount}
                  onChange={handleAmountChange}
                  disabled={buyFusdLoading}
                  placeholder={t("profilePage.buyFusdDialog.placeholder")}
                  className="w-full px-3 py-3 rounded-lg text-sm mono-num outline-none"
                  style={{
                    background: C.card,
                    border: `1px solid ${C.border}`,
                    color: C.text,
                  }}
                />
                <span
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs"
                  style={{ color: C.dim }}
                >
                  {getTokenName(USDT_ADDRESS)}
                </span>
              </div>
            </div>
            {numAmount > 0 && (
              <div
                className="rounded-lg p-3 text-xs space-y-1.5"
                style={{
                  background: "rgba(34,197,94,0.04)",
                  border: "1px solid rgba(34,197,94,0.15)",
                }}
              >
                <div className="flex justify-between">
                  <span style={{ color: C.dim }}>
                    {t("profilePage.buyFusdDialog.payUsdt", {
                      tokenNameUsdt: getTokenName(USDT_ADDRESS),
                    })}
                  </span>
                  <span className="mono-num" style={{ color: C.red }}>
                    -{numAmount.toFixed(2)} {getTokenName(USDT_ADDRESS)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: C.dim }}>
                    {t("profilePage.buyFusdDialog.receiveFusd", {
                      tokenName: getTokenName(CCC_FUSD_ADDRESS),
                    })}
                  </span>
                  <span className="mono-num" style={{ color: C.green }}>
                    +{(numAmount / fusdtPrice!).toFixed(2)}{" "}
                    {getTokenName(CCC_FUSD_ADDRESS)}
                  </span>
                </div>
              </div>
            )}
            <button
              className="w-full py-3 rounded-lg font-bold text-sm btn-primary"
              disabled={
                buyFusdLoading ||
                !amount ||
                numAmount <= 0 ||
                numAmount > Number(balanceFormatted)
              }
              onClick={handleConfirm}
            >
              {buyFusdLoading
                ? t("profilePage.buyFusdDialog.processing")
                : t("profilePage.buyFusdDialog.confirm")}
            </button>
          </motion.div>

          {/* {step === "pending" && (
            <motion.div
              key="pending"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center py-8 gap-3"
            >
              <RefreshCw
                size={28}
                className="animate-spin"
                style={{ color: C.primary }}
              />
              <p className="text-sm" style={{ color: C.muted }}>
                {t("profilePage.buyFusdDialog.processing")}
              </p>
            </motion.div>
          )} */}
          {/* {step === "success" && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center py-6 gap-3"
            >
              <CheckCircle size={40} style={{ color: C.green }} />
              <p className="font-semibold">
                {t("profilePage.buyFusdDialog.success")}
              </p>
              <p className="text-sm" style={{ color: C.muted }}>
                {t("profilePage.buyFusdDialog.credited", {
                  amount: numAmount.toFixed(2),
                })}
              </p>
              <button
                className="w-full py-3 rounded-lg font-bold text-sm btn-primary"
                onClick={handleClose}
              >
                {t("profilePage.buyFusdDialog.done")}
              </button>
            </motion.div>
          )} */}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
